import { useState } from 'react';
import { OverlayProvider, RootThemeProvider } from '@direct-frontend/components';
import { useEventListener, useIsomorphicLayoutEffect } from '@direct-frontend/stdlib';
import { HostnameOverrideCookies } from '@adv-frontend/yastore-modules/lib';
import { CommunicationModal, CommunicationProvider } from '#features/Communication';
import { CompareWarehousesOzonFbsModal } from '#features/CompareWarehousesOzonFbsModal';
import { alertSrc } from '#shared/assets';
import { SnowflakesWrapper as SnowflakesAsync } from '#shared/components/Snowflakes';
import { WhaleAnimationAsync } from '#shared/components/WhaleAnimation';
import { AutomaticPresentationProvider, AutomaticPresentationRouteBoundary } from '#shared/lib/automaticPresentation';
import { getRequestContext } from '#shared/lib/context';
import { TemporaryMountPoint } from '#shared/lib/dom';
import { useConfirm } from '#shared/lib/hooks';
import { MobileRouteSync } from '#shared/lib/platform';
import { checkIsExternalCheckout } from '#shared/lib/storeInfo';
import { useTheme } from '#shared/lib/theme';
import { ButterflyScript } from '../components/ButterflyScript';
import { ExternalRoutes } from '../components/ExternalRoutes';
import { Routes } from '../components/Routes';
import { ApplicationStub } from './ApplicationStub';
import './Application.css';

export const Application = () => {
    const { confirm, renderConfirmModal } = useConfirm();
    const theme = useTheme();
    const isExternalCheckout = checkIsExternalCheckout();
    const routeContent = isExternalCheckout ? <ExternalRoutes /> : <Routes />;

    useEventListener('userDoesNotHaveAnAccess' as never, () => {
        confirm({
            title: 'У вас нет доступа к данной странице',
            bodyText: 'Пожалуйста, обратитесь к администратору',
            applyText: 'Понятно',
            hideCancel: true,
            imageSrc: alertSrc,
        });
    });

    return (
        <AutomaticPresentationProvider>
            <AutomaticPresentationRouteBoundary>
                <RootThemeProvider theme={theme}>
                    <OverlayProvider style={{ height: '100%' }}>
                        {/* Смена платформы пересоздаёт страницы в MobileRouteSync, но сохраняет коммуникации. */}
                        <CommunicationProvider>
                            <CommunicationModal />
                            <MobileRouteSync>{routeContent}</MobileRouteSync>
                        </CommunicationProvider>
                        <TemporaryMountPoint />
                        <HostnameOverrideCookies env={getRequestContext().env} />
                        {renderConfirmModal()}
                        <SnowflakesAsync />
                        <WhaleAnimationAsync />
                        <ButterflyScript />
                        <RequiredOnAllPagesComponents />
                    </OverlayProvider>
                </RootThemeProvider>
            </AutomaticPresentationRouteBoundary>
        </AutomaticPresentationProvider>
    );
};

const RequiredOnAllPagesComponents = () => (
    <>
        <CompareWarehousesOzonFbsModal />
    </>
);

let globalIsLoaded = false;

export const ApplicationSuspended = () => {
    const [isLoaded, setIsLoaded] = useState(() => globalIsLoaded || getRequestContext().isSsrEnabled);

    useIsomorphicLayoutEffect(() => {
        globalIsLoaded = true;
        setIsLoaded(true);
    }, []);

    if (!isLoaded) {
        return <ApplicationStub />;
    }

    return <Application />;
};
